import { useEffect, useState } from 'react';
import { SearchBar } from './components/SearchBar';
import { TierFilter } from './components/TierFilter';
import { ResultList } from './components/ResultList';
import { GraphView } from './components/GraphView';
import { useSearch } from './hooks/useSearch';
import { useThemeMode } from './hooks/useThemeMode';
import { extractSourcePath } from './lib/formatters';
import type { Tier } from './lib/types';

const TOP_K_OPTIONS = [5, 10, 15, 25] as const;
const DEFAULT_TOP_K = 15;

export function App() {
  const { results, loading, error, timingMs, search } = useSearch();
  const [mode, setMode] = useThemeMode();
  const [tier, setTier] = useState<Tier | undefined>(undefined);
  const [topK, setTopK] = useState<number>(DEFAULT_TOP_K);
  const [lastQuery, setLastQuery] = useState<string>('');
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [hoverSlug, setHoverSlug] = useState<string | null>(null);

  const runSearch = (
    query: string,
    nextTier: Tier | undefined,
    nextTopK: number,
  ) => {
    setLastQuery(query);
    setActiveSlug(null);
    setHoverSlug(null);
    void search(query, { tier: nextTier, topK: nextTopK, expand: true });
  };

  const handleSubmit = (q: string) => runSearch(q, tier, topK);

  const handleTierChange = (next: Tier | undefined) => {
    setTier(next);
    if (lastQuery) runSearch(lastQuery, next, topK);
  };

  const handleTopKChange = (next: number) => {
    setTopK(next);
    if (lastQuery) runSearch(lastQuery, tier, next);
  };

  useEffect(() => {
    if (!activeSlug) return;
    const el = document.querySelector<HTMLElement>(`[data-slug="${CSS.escape(activeSlug)}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeSlug]);

  useEffect(() => {
    if (results.length === 0) return;
    // Diagnostic for source-link rendering — remove once verified on live data.
    // eslint-disable-next-line no-console
    console.table(
      results.map((r) => ({
        slug: r.slug,
        top_level: r.source_path ?? '',
        frontmatter: extractSourcePath(r.body_md ?? '') ?? '',
      })),
    );
  }, [results]);

  const showEmptyTier =
    !loading && !error && timingMs !== null && results.length === 0 && tier;

  return (
    <>
      <div className="section-narrow">
        <header className="app-header">
          <h1 className="app-title">Ryven Wiki</h1>
          <div className="header-controls">
            <label className="control">
              <span className="control-label">count</span>
              <select
                className="control-select"
                value={topK}
                onChange={(e) => handleTopKChange(Number(e.target.value))}
                disabled={loading}
                aria-label="results count"
              >
                {TOP_K_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="mode-toggle"
              data-mode={mode}
              onClick={() => setMode(mode === 'ambient' ? 'showcase' : 'ambient')}
              aria-label="toggle visual mode"
              title={mode === 'ambient' ? 'switch to showcase' : 'switch to ambient'}
            >
              {mode}
            </button>
          </div>
        </header>
      </div>

      <div className="section-narrow">
        <SearchBar onSubmit={handleSubmit} disabled={loading} />
        <TierFilter value={tier} onChange={handleTierChange} disabled={loading} />
        <div className="status-row">
          {loading && <span>searching…</span>}
          {error && <span className="status-error" role="alert">{error}</span>}
          {!loading && !error && timingMs !== null && results.length > 0 && (
            <span>
              {results.length} result{results.length === 1 ? '' : 's'} in {timingMs} ms
            </span>
          )}
        </div>
      </div>

      <div className="section-narrow section-graph">
        <GraphView
          results={results}
          activeSlug={activeSlug}
          hoverSlug={hoverSlug}
          onSelect={setActiveSlug}
          onHover={setHoverSlug}
        />
      </div>

      <main className="section-narrow">
        {showEmptyTier && (
          <div className="empty-state" role="status">
            No results in tier <em>{tier}</em> for <em>"{lastQuery}"</em> —{' '}
            <button type="button" onClick={() => handleTierChange(undefined)}>
              clear filter and show All
            </button>
          </div>
        )}
        <ResultList
          results={results}
          activeSlug={activeSlug}
          hoverSlug={hoverSlug}
          onSelect={setActiveSlug}
          onHover={setHoverSlug}
        />
      </main>
    </>
  );
}
